import React, { useEffect, useState } from "react";
import {
  FlatList,
  Modal,
  Button,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";
import { useDispatch, useSelector } from "react-redux";
import CommonService, { serviceResponse } from "../../../services/common";
import { getPositions, Position } from "../../../services/Position";
import { RootState } from "../../../services/store/app";
import { setLocalPositions } from "../../../services/store/common";
import { CloseIcon } from "../../Icons";
import { colors } from "../../Shared";

interface Props {
  isVisible: boolean;
  transparent?: boolean;
  onSelect: (position: Position) => void;
  onClose: () => void;
}

const commonService = new CommonService();

export default function PositionSelector({
  transparent = true,
  isVisible,
  onSelect,
  onClose,
}: Props) {
  const dispatch = useDispatch();
  const commonStore = useSelector((state: RootState) => state.commonReducer);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [positions, setPositions] = useState<Position[]>([]);
  const [position, setPosition] = useState<Position | null>(null);

  useEffect(() => {
    setPositions(commonStore.positions);
  }, [commonStore]);

  const unsubscribe = async () => {
    const req = await getPositions();

    if (req === serviceResponse.systemError) {
      return alert("Something went wrong, please try it again reloading");
    }

    if (req === serviceResponse.notContent) {
      return dispatch(setLocalPositions([]));
    }

    const data = req as Position[];
    dispatch(setLocalPositions(data));
  };

  useEffect(() => {
    unsubscribe();
  }, []);

  const filter = ({ isActive, name }: Position) => {
    if (!isActive) {
      return false;
    }

    if (
      commonService
        .replaceSpecialChars(name.toLowerCase())
        .match(search.toLowerCase())
    ) {
      return true;
    }

    return false;
  };

  const onSetPosition = (positionToSet: Position) => {
    if (position?.id === positionToSet.id) {
      return setPosition(null);
    }

    return setPosition(positionToSet);
  };

  const renderItems = () => {
    if (!positions.length) {
      return (
        <View style={{ flex: 1, alignItems: "center", marginTop: 10 }}>
          <Text>Without positions to show</Text>
        </View>
      );
    }

    return (
      <FlatList
        data={positions.filter(filter)}
        style={{ flex: 1, padding: 5 }}
        ListEmptyComponent={
          <View style={{ flex: 1, alignItems: "center", marginTop: 10 }}>
            <Text>Without results</Text>
          </View>
        }
        renderItem={({ item }) => (
          <Pressable
            onPress={() => onSetPosition(item)}
            style={[
              styles.itemContainer,
              {
                backgroundColor:
                  position?.id === item.id ? colors.bg.medium : colors.bg.light,
              },
            ]}
            key={item.id}
          >
            <Text style={{ textTransform: "capitalize" }}>{item.name}</Text>
          </Pressable>
        )}
      />
    );
  };

  const next = async () => {
    if (isLoading) {
      return;
    }

    setIsLoading(true);

    if (!position) {
      return;
    }

    onSelect(position);
    onClose();
  };

  return (
    <Modal transparent={transparent} visible={isVisible}>
      <View style={styles.popUp}>
        <View style={styles.container}>
          <View style={styles.headerContainer}>
            <TextInput
              value={search}
              onChangeText={setSearch}
              style={styles.headerInput}
              placeholder="search"
            />

            {search.length > 0 && (
              <Pressable style={styles.headerIcon} onPress={()=>setSearch("")}>
                <CloseIcon size={15} />
              </Pressable>
            )}
          </View>

          {renderItems()}

          {positions.length > 0 && (
            <View style={styles.footerContainer}>
              <Button title="cancel" onPress={onClose} />
              <View style={{ height: 5 }} />
              <Button
                title="continue"
                onPress={next}
                color={colors.bg.primary}
                disabled={position === null}
              />
            </View>
          )}
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  popUp: {
    backgroundColor: "#00000030",
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  container: {
    width: 400,
    height: "70%",
    backgroundColor: colors.bg.white,
    borderRadius: 5,
  },
  headerContainer: {
    backgroundColor: colors.bg.light,
    height: 50,
    flexDirection: "row",
    padding: 5,
    borderTopRightRadius: 5,
    borderTopLeftRadius: 5,
  },
  headerIcon: {
    position: "absolute",
    top: 17.5,
    right: 15,
  },
  headerInput: {
    flex: 1,
    backgroundColor: colors.bg.white,
    paddingLeft: 10,
    borderRadius: 5,
  },
  footerContainer: {
    padding: 5,
  },
  footerButton: {
    width: 192.5,
    height: 60,
    borderRadius: 5,
    justifyContent: "center",
    alignItems: "center",
  },
  itemContainer: {
    justifyContent: "center",
    height: 40,
    marginTop: 5,
    padding: 5,
  },
});
